// Craft — 3x3 ingredient grid + technique slot. Kids can drop up
// to 9 ingredients on the workspace and pick one cooking technique
// to apply. The edge function looks up the sorted-ingredients
// cache key; on miss, Haiku gets the full list and returns a real
// food name.
//
// Slot routing on tap:
//   * Tap a technique tile → goes to the HOW slot (toggles).
//   * Tap an ingredient → fills the next empty cell in the 3x3
//     grid (row-major). If the grid is full, replaces the most
//     recent slot.
//   * Tap a filled cell → clears it.
//
// EMOJI-IN-UI EXCEPTION: this tree is the explicit opt-in.

import { useRef, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import { readAiTokenDenial, KID_TOKENS_MESSAGE } from "../../lib/aiTokenError";
import { useKidCraft } from "../../lib/useKidCraft";
import { KID_TECHNIQUES, KNIFE_VARIANTS } from "./kidTools";
import { KidRecipeBook } from "./KidRecipeBook";
import { ButcherShop } from "./ButcherShop";
import { PinnedSearch } from "./PinnedSearch";
import { KidColoringCanvas } from "./KidColoringCanvas";
import { KidSvgColoring } from "./KidSvgColoring";
import { PinPad } from "./PinPad";
import KidGenerationLockscreen from "./KidGenerationLockscreen";

function craftSlug(name) {
  return "craft:" + (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60)
    || "craft:dish";
}

const TECHNIQUE_EMOJI_SET = new Set(KID_TECHNIQUES.map((t) => t.emoji));
const GRID_CAPACITY = 9;
const EMPTY_GRID = Array(GRID_CAPACITY).fill(null);
const MAX_TECHS = 3;

export function Craft({ userId }) {
  const { inventory, combine, combining, error } = useKidCraft(userId);
  const [grid, setGrid] = useState(EMPTY_GRID);
  const [techs, setTechs] = useState([]);
  const [result, setResult] = useState(null);
  const [askState, setAskState] = useState("idle");
  const [query, setQuery] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [butcherOpen, setButcherOpen] = useState(false);
  const [knifeMenuOpen, setKnifeMenuOpen] = useState(false);
  const knifeHoldTimer = useRef(null);
  // Coloring page state. colorState: idle | generating | ready | error.
  // colorPage: { id, lineArtURL, petName, comboName } once generated.
  const [colorState, setColorState] = useState("idle");
  const [colorPage,  setColorPage]  = useState(null);
  const [colorError, setColorError] = useState(null);
  // bgGenerating — true while a coloring page edge fn call is in
  // flight (placeholder inserted, kid-color-craft still running).
  // Rate limit: COLOR THIS is disabled while this is true so a kid
  // spamming the button can't fire 5 parallel gens at $0.20 each.
  // Independent of colorState (which drops to idle so the kid can
  // keep playing) — this stays sticky until the edge fn returns.
  const [bgGenerating, setBgGenerating] = useState(false);
  // (Search input scroll-into-view on focus is handled globally
  // by the single root focusin listener in useKeyboardOffset —
  // no per-input ref or hook needed. Per 2026-05-23 audit,
  // commit 5e/5.)

  const isTech = (item) => item?.isTool || TECHNIQUE_EMOJI_SET.has(item?.emoji);
  const filledCount = grid.filter(Boolean).length;
  const isTechActive = (item) =>
    techs.some((t) => t.emoji === item.emoji && t.name === item.name);

  const pickItem = (item) => {
    if (combining) return;
    if (isTech(item)) {
      // Toggle technique in/out of the active set (up to MAX_TECHS).
      if (isTechActive(item)) {
        setTechs(techs.filter((t) => !(t.emoji === item.emoji && t.name === item.name)));
      } else if (techs.length < MAX_TECHS) {
        setTechs([...techs, { emoji: item.emoji, name: item.name }]);
      }
      return;
    }
    // Fill first empty cell; if full, push from the front.
    const idx = grid.findIndex((c) => !c);
    if (idx === -1) {
      setGrid([...grid.slice(1), item]);
    } else {
      const next = [...grid];
      next[idx] = item;
      setGrid(next);
    }
  };

  const clearCell = (i) => {
    if (combining) return;
    const next = [...grid];
    next[i] = null;
    setGrid(next);
  };

  const removeTech = (idx) => {
    if (combining) return;
    setTechs(techs.filter((_, i) => i !== idx));
  };

  const reset = () => {
    setGrid(EMPTY_GRID);
    setTechs([]);
  };

  // Combine REQUIRES a technique always. Even "Bread + Cheese →
  // Sandwich" — the kid must pick Mix (or Bake, Grill, etc.) to
  // commit to an action. Implicit "just combine" is banned —
  // every action is a verb the kid chooses.
  const canCombine = filledCount >= 1 && techs.length >= 1;

  const doCombine = async () => {
    const items = grid.filter(Boolean);
    if (!canCombine) return;
    const r = await combine(items, techs);
    if (r) setResult(r);
  };

  const askGrownUp = async () => {
    if (!result || !userId || askState !== "idle") return;
    setAskState("sending");
    const slug = craftSlug(result.name);
    // Pull the exact ingredients + technique that produced this
    // result so the parent inbox has the canonicals it needs to
    // feed into the recipe generator.
    const ingredientList = grid.filter(Boolean).map((i) => ({
      emoji: i.emoji,
      name:  i.name,
    }));
    const { error: insErr } = await supabase.from("kid_food_requests").insert({
      kid_user_id:      userId,
      canonical_id:     slug,
      recipe_slug:      slug,
      request_kind:     "recipe",
      food_emoji:       result.emoji,
      food_name:        result.name,
      ingredients:      ingredientList,
      technique_emoji:  techs.map((t) => t.emoji).join("") || null,
      technique_name:   techs.map((t) => t.name).filter(Boolean).join(" + ") || null,
      result_image_url: result.imageUrl || null,
    });
    if (insErr) {
      console.warn("[craft] ask failed:", insErr.message);
      const msg = String(insErr.message || "");
      if (msg.includes("rate_limit_duplicate")) {
        setAskState("dupe");
      } else if (msg.includes("rate_limit_daily")) {
        setAskState("daily");
      } else {
        setAskState("idle");
      }
      return;
    }
    setAskState("sent");
  };

  const closeResult = () => {
    setResult(null);
    setAskState("idle");
    setColorState("idle");
    setColorError(null);
    reset();
  };

  // COLOR THIS is a paid action (~$0.16-0.21 per page). Gate behind
  // the kid's exit PIN so a kid can't spam-generate. Two paths:
  //
  //   - Kid taps COLOR THIS → check if PIN is set:
  //       PIN set     → show PinPad, on success → runColorRequest()
  //       PIN not set → show inline "ask a grown-up to set a PIN
  //                     first" message; do nothing else
  //   - Same exit-PIN that gates leaving Kids Mode. Re-using one
  //     credential, one PIN to remember.
  const [pinGateOpen, setPinGateOpen] = useState(false);

  // Background generation pattern. Was: fullscreen lockscreen until
  // the edge fn returned (10-15s). Now: insert a placeholder row
  // immediately so the gallery renders an animated tile, fire the
  // edge fn fire-and-forget, and drop the UI back to idle so the kid
  // can keep playing. The placeholder row's line_art_url is null;
  // the edge fn UPDATEs the row with the URL when ready, the
  // realtime sub on useKidColoringPages picks it up, the gallery
  // tile swaps from animated placeholder to a real preview.
  //
  // On failure: we delete the placeholder row so a busted generation
  // doesn't leave a permanent loading tile in the gallery.
  const runColorRequest = async () => {
    if (!result || !userId) return;
    if (bgGenerating) return; // hard guard against double-fire
    setColorError(null);
    const ingredientEmojis = grid.filter(Boolean).map((i) => i.emoji);
    const sortedEmojis = [...ingredientEmojis].sort();
    // 0. Cache check — never regenerate the SAME combo. Look for an
    //    existing row keyed on (kid, both emojis, combo_name). If
    //    found, jump straight to that page (in-progress placeholder
    //    OR finished line art) instead of burning another $0.20 of
    //    gpt-image-2 + Recraft. Was previously letting the kid spam
    //    COLOR THIS on Cheesy Pasta and pay for a fresh draw each
    //    time. The same-canonical row already lives in the gallery
    //    so this just opens it; no UI confusion.
    {
      const { data: existing } = await supabase
        .from("kid_coloring_pages")
        .select("id, line_art_url, line_art_svg_url, source_image_url, combo_name")
        .eq("kid_user_id", userId)
        .eq("combo_name", result.name)
        .eq("combo_a_emoji", sortedEmojis[0] || null)
        .eq("combo_b_emoji", sortedEmojis[1] || null)
        .not("line_art_url", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (existing?.id && existing.line_art_url) {
        setColorPage({
          id:             existing.id,
          lineArtURL:     existing.line_art_url,
          lineArtSvgURL:  existing.line_art_svg_url || null,
          petName:        null,
          comboName:      existing.combo_name || result.name,
        });
        setColorState("ready");
        return;
      }
    }
    // 1. Insert placeholder row. line_art_url stays null (migration
    //    0254 dropped the NOT NULL) so the gallery's placeholder
    //    branch picks it up. Combo metadata lands now so the tile
    //    can show what's being drawn even before the URL fills in.
    const { data: placeholder, error: phErr } = await supabase
      .from("kid_coloring_pages")
      .insert({
        kid_user_id:   userId,
        combo_a_emoji: sortedEmojis[0] || null,
        combo_b_emoji: sortedEmojis[1] || null,
        combo_name:    result.name,
      })
      .select("id")
      .single();
    if (phErr || !placeholder?.id) {
      console.warn("[craft] placeholder insert failed:", phErr?.message);
      setColorError("couldn't start drawing");
      setColorState("error");
      return;
    }
    // 2. Fire-and-forget. Don't block UI; we never await this.
    //    But we DO flip bgGenerating so COLOR THIS is disabled
    //    until the edge fn returns — prevents spam-tapping firing
    //    parallel $0.20 generations.
    setColorState("idle");
    setColorPage(null);
    setBgGenerating(true);
    void (async () => {
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke("kid-color-craft", {
          body: {
            kidUserId:      userId,
            craftName:      result.name,
            comboEmojis:    ingredientEmojis,
            includePet:     true,
            existingPageId: placeholder.id,
          },
        });
        if (invokeErr || data?.error) {
          const msg = invokeErr?.message || data?.error || "couldn't draw it";
          console.warn("[craft] color failed:", msg);
          // Weekly AI-token allowance exhausted (docs/AI_TOKENS.md) —
          // tell the kid honestly instead of failing silently.
          const denial = await readAiTokenDenial(invokeErr, data);
          if (denial) {
            setColorError(KID_TOKENS_MESSAGE);
            setColorState("error");
          }
          // Tear down the placeholder so it doesn't stick in the gallery.
          await supabase.from("kid_coloring_pages")
            .delete()
            .eq("id", placeholder.id)
            .eq("kid_user_id", userId);
        }
        // Success path is no-op from the UI's POV: the edge fn
        // already UPDATEd the row, realtime will deliver to the
        // gallery on its own.
      } catch (e) {
        console.warn("[craft] color invoke threw:", e?.message || e);
        await supabase.from("kid_coloring_pages")
          .delete()
          .eq("id", placeholder.id)
          .eq("kid_user_id", userId);
      } finally {
        setBgGenerating(false);
      }
    })();
  };

  const colorThis = async () => {
    if (!result || !userId || colorState !== "idle") return;
    // Check if the kid has a PIN set. has_kid_exit_pin returns
    // true/false based on whether kid_exit_pins has a row for this id.
    const { data: hasPin, error: probeErr } = await supabase.rpc("has_kid_exit_pin", {
      p_user_id: userId,
    });
    if (probeErr) {
      console.warn("[craft] has_kid_exit_pin probe:", probeErr.message);
      setColorError("couldn't check the PIN");
      setColorState("error");
      return;
    }
    if (!hasPin) {
      setColorError("Ask a grown-up to set a PIN in Settings → Kids first. (Stops accidental tappy-tap.)");
      setColorState("error");
      return;
    }
    // PIN is set → open the gate.
    setPinGateOpen(true);
  };

  const closeColoringCanvas = () => {
    setColorPage(null);
    setColorState("idle");
    setColorError(null);
  };

  const techniqueItems = KID_TECHNIQUES.map((t) => ({
    emoji: t.emoji,
    name:  t.displayName,
    isTool: true,
  }));
  const q = query.trim().toLowerCase();
  const ingredientItems = inventory
    .filter((i) => !isTech(i))
    .filter((i) => !q || (i.name || "").toLowerCase().includes(q));

  return (
    <div
      style={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top — 3x3 grid + technique slot + COMBINE */}
      <div style={{ padding: "0 16px 10px", flexShrink: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            maxWidth: 200,
            margin: "0 auto 8px",
          }}
        >
          {grid.map((cell, i) => (
            <GridCell
              key={i}
              item={cell}
              onClear={() => clearCell(i)}
            />
          ))}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 18, color: COLOR.muted, lineHeight: 1 }}>↓</span>
          <TechniqueSlots items={techs} onRemove={removeTech} />
        </div>

        {/* Recipe Book button — top-right corner of the slot area.
            Floats above the COMBINE button, opens the Book overlay
            so Peezy can find past discoveries + tap MAKE AGAIN. */}
        <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <button
            type="button"
            onClick={() => setBookOpen(true)}
            className="mcm-focusable"
            style={{
              flex: 1,
              padding: "10px 14px",
              background: "rgba(255,255,255,0.85)",
              color: COLOR.ink,
              border: `1.5px solid ${COLOR.goldDim}`,
              borderRadius: 12,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: "pointer",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            📖 RECIPE BOOK
          </button>
          <button
            type="button"
            onClick={() => setButcherOpen(true)}
            className="mcm-focusable"
            style={{
              flex: 1,
              padding: "10px 14px",
              background: "rgba(255,255,255,0.85)",
              color: COLOR.ink,
              border: `1.5px solid ${COLOR.goldDim}`,
              borderRadius: 12,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: "pointer",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            🥩 BUTCHER
          </button>
        </div>

        <button
          type="button"
          onClick={doCombine}
          disabled={!canCombine || combining}
          className="mcm-focusable"
          style={{
            width: "100%",
            padding: "12px 16px",
            background: (!canCombine || combining) ? COLOR.leafDim : COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: 14,
            fontFamily: FONT.mono,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.1em",
            cursor: (!canCombine || combining) ? "not-allowed" : "pointer",
            boxShadow: (!canCombine || combining) ? "none" : `0 6px 16px ${COLOR.leafDim}`,
            touchAction: "manipulation",
            transition: "background 200ms ease",
          }}
        >
          {combining
            ? "✨ COMBINING…"
            : filledCount === 0
              ? "🧪 PICK A FOOD!"
              : techs.length === 0
                ? "🧪 PICK A TECHNIQUE!"
                : `🧪 COMBINE ${filledCount}!`}
        </button>
        {error && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 12,
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Technique row */}
      <div style={{ padding: "0 12px 8px", flexShrink: 0 }}>
        <SectionLabel label="HOW TO COOK" />
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 4,
          }}
        >
          {techniqueItems.map((t) => {
            const active = isTechActive(t);
            const isKnife = t.emoji === "🔪";
            // For the knife tile, also check if a knife VARIANT is
            // active so the tile reflects state.
            const knifeVariantActive = isKnife && KNIFE_VARIANTS.some((v) =>
              v.emoji !== "🔪" && techs.some((tt) => tt.emoji === v.emoji));

            const onPointerDown = isKnife ? () => {
              if (knifeHoldTimer.current) clearTimeout(knifeHoldTimer.current);
              knifeHoldTimer.current = setTimeout(() => {
                setKnifeMenuOpen(true);
                knifeHoldTimer.current = null;
              }, 450);
            } : undefined;
            const onPointerUp = isKnife ? () => {
              if (knifeHoldTimer.current) {
                clearTimeout(knifeHoldTimer.current);
                knifeHoldTimer.current = null;
                // Quick tap → default chop
                pickItem(t);
              }
            } : undefined;
            const onPointerLeave = isKnife ? () => {
              if (knifeHoldTimer.current) {
                clearTimeout(knifeHoldTimer.current);
                knifeHoldTimer.current = null;
              }
            } : undefined;

            return (
              <button
                key={t.emoji + t.name}
                type="button"
                onClick={isKnife ? undefined : () => pickItem(t)}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerLeave}
                style={{
                  all: "unset",
                  cursor: "pointer",
                  flexShrink: 0,
                  width: 64,
                  height: 64,
                  background: (active || knifeVariantActive) ? COLOR.goldDeep : "#fff",
                  border: `2px solid ${(active || knifeVariantActive) ? COLOR.gold : COLOR.border}`,
                  borderRadius: 14,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  touchAction: "manipulation",
                  boxShadow: (active || knifeVariantActive) ? `0 2px 8px ${COLOR.goldDeep}` : "none",
                  position: "relative",
                }}
              >
                <span style={{ fontSize: 24, lineHeight: 1 }}>{t.emoji}</span>
                <span
                  style={{
                    fontFamily: FONT.mono,
                    fontSize: 8,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    color: (active || knifeVariantActive) ? COLOR.gold : COLOR.ink,
                    textAlign: "center",
                  }}
                >
                  {t.name.toUpperCase()}
                </span>
                {isKnife && (
                  <span style={{
                    position: "absolute",
                    bottom: 2,
                    right: 4,
                    fontSize: 8,
                    color: (active || knifeVariantActive) ? COLOR.gold : COLOR.sub,
                    fontFamily: FONT.mono,
                    fontWeight: 700,
                  }}>▾</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Ingredients */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          padding: "0 12px 12px",
        }}
      >
        <SectionLabel label="INGREDIENTS" />
        <div style={{ marginBottom: 6 }}>
          <PinnedSearch value={query} onChange={setQuery} placeholder="Search ingredients…" />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(78px, 1fr))",
            gap: 8,
          }}
        >
          {ingredientItems.map((item) => (
            <InventoryTile
              key={`${item.emoji}-${item.name}`}
              item={item}
              onClick={() => pickItem(item)}
              dimmed={grid.some((c) => c?.emoji === item.emoji && c?.name === item.name)}
            />
          ))}
        </div>
      </div>

      {/* Hide the ResultOverlay while the PIN gate is open so the
          kid (a) sees ONE modal at a time and (b) can't accidentally
          tap ResultOverlay's DONE button (which closeResult()'s the
          result, and runColorRequest's first line is `if (!result)
          return;` → silent bail after PIN success). PinPad cancel
          flips pinGateOpen=false → ResultOverlay re-mounts and the
          kid returns to the result they were looking at. */}
      {result && colorState !== "ready" && !pinGateOpen && (
        <ResultOverlay
          result={result}
          onClose={closeResult}
          askState={askState}
          onAsk={askGrownUp}
          colorState={colorState}
          colorError={colorError}
          onColor={colorThis}
          bgGenerating={bgGenerating}
        />
      )}

      {colorState === "ready" && colorPage && (colorPage.lineArtSvgURL ? (
        <KidSvgColoring
          kidUserId={userId}
          coloringPageId={colorPage.id}
          lineArtSvgURL={colorPage.lineArtSvgURL}
          petName={colorPage.petName}
          comboName={colorPage.comboName}
          onClose={closeColoringCanvas}
        />
      ) : (
        <KidColoringCanvas
          kidUserId={userId}
          coloringPageId={colorPage.id}
          lineArtURL={colorPage.lineArtURL}
          petName={colorPage.petName}
          comboName={colorPage.comboName}
          onClose={closeColoringCanvas}
        />
      ))}

      {/* Parent PIN gate before the paid coloring-page generation
          fires. Reuses the same PIN as Kids Mode exit. On success →
          runColorRequest(); on cancel → bail back to ResultOverlay. */}
      {pinGateOpen && (
        <PinPad
          kidId={userId}
          onCancel={() => setPinGateOpen(false)}
          onSuccess={() => {
            setPinGateOpen(false);
            runColorRequest();
          }}
        />
      )}

      {/* Lockscreen retained ONLY for the error branch. The generating
          state used to mount this fullscreen; now generation runs in
          the background (a placeholder tile lands in the gallery and
          the edge fn fills it in), so the kid keeps using the app
          while the AI works. Only fatal "couldn't even start drawing"
          errors block the screen — the placeholder write would have
          to fail server-side for this to show. */}
      <KidGenerationLockscreen
        visible={colorState === "error"}
        title="Couldn't start drawing"
        subtitle={result?.name ? `${result.name} and your pet, together` : "Pets and crayons coming together"}
        error={colorError}
        onRetry={runColorRequest}
        onCancel={() => { setColorState("idle"); setColorError(null); }}
      />

      {butcherOpen && (
        <ButcherShop onClose={() => setButcherOpen(false)} />
      )}

      {knifeMenuOpen && (
        <KnifeVariantMenu
          activeTechs={techs}
          onPick={(variant) => {
            // Remove any other knife variant currently active, then
            // add the picked one. Single knife-action at a time.
            const otherTechs = techs.filter((t) =>
              !KNIFE_VARIANTS.some((v) => v.emoji === t.emoji)
            );
            const next = [...otherTechs, { emoji: variant.emoji, name: variant.displayName }];
            setTechs(next.slice(0, MAX_TECHS));
            setKnifeMenuOpen(false);
          }}
          onClose={() => setKnifeMenuOpen(false)}
        />
      )}

      {bookOpen && (
        <KidRecipeBook
          userId={userId}
          onClose={() => setBookOpen(false)}
          onMakeAgain={({ ingredients, technique }) => {
            // Fill the grid with the recipe's ingredients (skip techniques)
            const techSet = new Set(KID_TECHNIQUES.map((t) => t.emoji));
            const items = ingredients
              .filter((i) => !techSet.has(i.emoji))
              .slice(0, 9);
            const next = [...EMPTY_GRID];
            items.forEach((it, idx) => { next[idx] = { emoji: it.emoji, name: it.name }; });
            setGrid(next);
            // Back-compat: technique may be a single object (legacy
            // rows) or already an array of {emoji,name}.
            if (Array.isArray(technique)) {
              setTechs(technique.filter((t) => t?.emoji).slice(0, MAX_TECHS));
            } else if (technique?.emoji) {
              setTechs([{ emoji: technique.emoji, name: technique.name }]);
            } else {
              setTechs([]);
            }
          }}
        />
      )}
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div
      style={{
        fontFamily: FONT.mono,
        fontSize: 9,
        letterSpacing: "0.14em",
        fontWeight: 700,
        color: COLOR.sub,
        marginBottom: 6,
      }}
    >
      {label}
    </div>
  );
}

function GridCell({ item, onClear }) {
  if (!item) {
    return (
      <div
        style={{
          aspectRatio: "1 / 1",
          background: "rgba(255,255,255,0.5)",
          border: `1.5px dashed ${COLOR.border}`,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          color: COLOR.sub,
        }}
      >
        ·
      </div>
    );
  }
  const hasImage = !!item.imageUrl;
  return (
    <button
      type="button"
      onClick={onClear}
      aria-label="clear"
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        aspectRatio: "1 / 1",
        background: hasImage
          ? `url(${item.imageUrl}) center / cover no-repeat`
          : "#fff",
        border: `1.5px solid ${COLOR.leaf}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: hasImage ? "flex-end" : "center",
        gap: 0,
        overflow: "hidden",
        position: "relative",
        boxShadow: `0 1px 4px ${COLOR.leafDim}`,
        touchAction: "manipulation",
      }}
    >
      {!hasImage && <span style={{ fontSize: 22, lineHeight: 1 }}>{item.emoji}</span>}
      {hasImage && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.6) 100%)",
          }}
        />
      )}
      <span
        style={{
          position: "relative",
          fontFamily: FONT.forma,
          fontSize: 8,
          fontWeight: 700,
          color: hasImage ? "#fff" : COLOR.ink,
          textAlign: "center",
          maxWidth: "92%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          padding: hasImage ? "0 3px 3px" : 0,
          textShadow: hasImage ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
        }}
      >
        {item.name}
      </span>
    </button>
  );
}

function KnifeVariantMenu({ activeTechs, onPick, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 1050,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: COLOR.paper,
          borderRadius: "18px 18px 0 0",
          padding: 22,
        }}
      >
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: COLOR.sub,
            fontWeight: 700,
            marginBottom: 6,
            textAlign: "center",
          }}
        >
          KNIFE TECHNIQUE
        </div>
        <div
          style={{
            fontFamily: FONT.detail,
            fontStyle: "italic",
            fontSize: 22,
            fontWeight: 600,
            color: COLOR.ink,
            marginBottom: 16,
            textAlign: "center",
          }}
        >
          Pick your cut
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          {KNIFE_VARIANTS.map((v) => {
            const active = activeTechs.some((t) => t.emoji === v.emoji);
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => onPick(v)}
                className="mcm-focusable"
                style={{
                  all: "unset",
                  cursor: "pointer",
                  flex: "1 1 100px",
                  minWidth: 100,
                  padding: "14px 10px",
                  background: active ? COLOR.goldDeep : "#fff",
                  border: `2px solid ${active ? COLOR.gold : COLOR.border}`,
                  borderRadius: 14,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  touchAction: "manipulation",
                }}
              >
                <span style={{ fontSize: 32, lineHeight: 1 }}>{v.emoji}</span>
                <span
                  style={{
                    fontFamily: FONT.mono,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: active ? COLOR.gold : COLOR.ink,
                  }}
                >
                  {v.displayName.toUpperCase()}
                </span>
                <span
                  style={{
                    fontFamily: FONT.detail,
                    fontStyle: "italic",
                    fontSize: 11,
                    color: COLOR.sub,
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {v.funFact}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TechniqueSlots({ items, onRemove }) {
  if (!items || items.length === 0) {
    return (
      <div
        style={{
          minWidth: 120,
          height: 44,
          padding: "0 10px",
          background: "rgba(255,255,255,0.5)",
          border: `1.5px dashed ${COLOR.goldDim}`,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT.mono,
          fontSize: 9,
          letterSpacing: "0.1em",
          color: COLOR.sub,
          fontWeight: 700,
        }}
      >
        HOW? (PICK UP TO 3)
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {items.map((item, i) => (
        <button
          key={`${item.emoji}-${item.name}-${i}`}
          type="button"
          onClick={() => onRemove(i)}
          aria-label={`remove ${item.name}`}
          className="mcm-focusable"
          style={{
            all: "unset",
            cursor: "pointer",
            height: 44,
            padding: "0 10px",
            background: COLOR.goldDeep,
            border: `1.5px solid ${COLOR.gold}`,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            touchAction: "manipulation",
            boxShadow: `0 2px 8px ${COLOR.goldDeep}`,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>{item.emoji}</span>
          <span
            style={{
              fontFamily: FONT.mono,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: COLOR.gold,
            }}
          >
            {item.name.toUpperCase()}
          </span>
        </button>
      ))}
      {items.length < 3 && (
        <span
          style={{
            fontFamily: FONT.mono,
            fontSize: 16,
            color: COLOR.sub,
            padding: "0 4px",
          }}
        >
          +
        </span>
      )}
    </div>
  );
}

function InventoryTile({ item, onClick, dimmed }) {
  const hasImage = !!item.imageUrl;
  const isHighlight = item.isFirstDiscovery || item.pinned;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={item.name}
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        aspectRatio: "1 / 1",
        background: hasImage
          ? `url(${item.imageUrl}) center / cover no-repeat`
          : item.pinned ? "#FFF8E7" : "#fff",
        border: `1.5px solid ${item.isFirstDiscovery ? COLOR.gold : item.pinned ? COLOR.goldDim : COLOR.border}`,
        borderRadius: RADIUS.xxl,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: hasImage ? "flex-end" : "center",
        alignItems: "center",
        position: "relative",
        opacity: dimmed ? 0.35 : 1,
        touchAction: "manipulation",
        boxShadow: isHighlight ? `0 2px 8px ${COLOR.goldDeep}` : "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {item.isFirstDiscovery && (
        <span
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            fontSize: 16,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
            zIndex: 2,
          }}
        >
          ⭐
        </span>
      )}
      {item.pinned && !item.isFirstDiscovery && (
        <span
          style={{
            position: "absolute",
            top: 3,
            right: 4,
            fontSize: 10,
            opacity: 0.8,
            zIndex: 2,
          }}
        >
          📌
        </span>
      )}
      {hasImage ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "45%",
            background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)",
          }}
        />
      ) : (
        <span style={{ fontSize: 32, lineHeight: 1, marginBottom: 2 }}>{item.emoji}</span>
      )}
      <span
        style={{
          position: "relative",
          fontFamily: FONT.forma,
          fontSize: 9,
          fontWeight: hasImage ? 700 : 600,
          color: hasImage ? "#fff" : COLOR.ink,
          textAlign: "center",
          lineHeight: 1.1,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          padding: hasImage ? "0 3px 5px" : 0,
          opacity: hasImage ? 1 : 0.85,
          textShadow: hasImage ? "0 1px 2px rgba(0,0,0,0.7)" : "none",
        }}
      >
        {item.name}
      </span>
    </button>
  );
}

function ResultOverlay({ result, onClose, askState, onAsk, colorState, colorError, onColor, bgGenerating }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.6)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 24,
        cursor: "pointer",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: result.isFirstDiscovery ? "#FFF8E7" : "#FAF6EE",
          border: `3px solid ${result.isFirstDiscovery ? COLOR.gold : COLOR.leaf}`,
          borderRadius: 28,
          padding: "32px 28px",
          maxWidth: 360,
          width: "100%",
          textAlign: "center",
          boxShadow: result.isFirstDiscovery
            ? `0 12px 40px ${COLOR.goldDeep}`
            : `0 12px 40px ${COLOR.leafDim}`,
          animation: "kid-craft-pop 360ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {result.isFirstDiscovery && (
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 11,
              letterSpacing: "0.16em",
              fontWeight: 700,
              color: COLOR.gold,
              marginBottom: 8,
            }}
          >
            ⭐ FIRST DISCOVERY! ⭐
          </div>
        )}
        {result.imageUrl ? (
          <div
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              maxWidth: 220,
              margin: "0 auto 14px",
              background: `url(${result.imageUrl}) center / cover no-repeat`,
              borderRadius: 20,
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
              animation: "kid-craft-spin 600ms ease-out",
            }}
            aria-label={result.name}
          />
        ) : (
          <div
            style={{
              fontSize: 120,
              lineHeight: 1,
              marginBottom: 14,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.15))",
              animation: "kid-craft-spin 600ms ease-out",
            }}
          >
            {result.emoji}
          </div>
        )}
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 28,
            color: COLOR.ink,
            marginBottom: 12,
            lineHeight: 1.2,
          }}
        >
          {result.name}
        </div>

        {/* Byproduct — many real processes yield a second food.
            Display it smaller, side-by-side with a "+ALSO" label
            so the kid clearly sees they got TWO things. */}
        {result.secondaryEmoji && result.secondaryName && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 16px",
              background: "rgba(255,255,255,0.85)",
              border: `1.5px dashed ${COLOR.goldDim}`,
              borderRadius: 14,
              marginBottom: 14,
              animation: "kid-xp-pop 480ms cubic-bezier(0.34, 1.56, 0.64, 1) 380ms backwards",
            }}
          >
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.12em",
                color: COLOR.sub,
              }}
            >
              + ALSO
            </span>
            <span style={{ fontSize: 36, lineHeight: 1 }}>{result.secondaryEmoji}</span>
            <span
              style={{
                fontFamily: FONT.itemTitle,
                fontStyle: "italic",
                fontSize: 18,
                color: COLOR.ink,
                lineHeight: 1.1,
              }}
            >
              {result.secondaryName}
            </span>
          </div>
        )}

        {/* XP badge — pops on every discovery worth XP. */}
        {result.xpGained > 0 && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              background: result.leveledUp ? COLOR.gold : COLOR.leaf,
              color: "#fff",
              borderRadius: 999,
              fontFamily: FONT.mono,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: "0.08em",
              marginBottom: 16,
              boxShadow: `0 4px 12px ${result.leveledUp ? COLOR.goldDeep : COLOR.leafDim}`,
              animation: "kid-xp-pop 480ms cubic-bezier(0.34, 1.56, 0.64, 1) 200ms backwards",
            }}
          >
            <span style={{ fontSize: 16 }}>✨</span>
            +{result.xpGained} XP
            {result.leveledUp && (
              <span style={{ marginLeft: 4 }}>
                · LEVEL {result.newLevel}!
              </span>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onAsk}
          disabled={askState !== "idle"}
          className="mcm-focusable"
          style={{
            width: "100%",
            padding: 16,
            background: askState === "sent" ? COLOR.leafDeep : COLOR.leaf,
            color: askState === "sent" ? COLOR.leaf : "#fff",
            border: askState === "sent" ? `1.5px solid ${COLOR.leaf}` : "none",
            borderRadius: 14,
            fontFamily: FONT.mono,
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.1em",
            cursor: askState === "idle" ? "pointer" : "default",
            marginBottom: 8,
            boxShadow: askState === "idle" ? `0 6px 16px ${COLOR.leafDim}` : "none",
            touchAction: "manipulation",
          }}
        >
          {askState === "sending"
            ? "ASKING…"
            : askState === "sent"
              ? "✓ ASKED A GROWN-UP!"
              : askState === "dupe"
                ? "💤 ALREADY ASKED!"
                : askState === "daily"
                  ? "🛑 TOO MANY ASKS TODAY"
                  : "🙋 ASK A GROWN-UP TO MAKE IT!"}
        </button>
        {/* COLOR THIS — generates a line-art coloring page with the
            kid's pet in the scene. ~10-15s for gpt-image-2; show
            spinner state during the wait. */}
        {(() => {
          // Disable while a background gen is in flight so the kid
          // can't spam-tap and fire $0.20 parallel renders. Idle &
          // error both allow taps; bgGenerating sticks until the
          // edge fn resolves.
          const disabled = bgGenerating || (colorState !== "idle" && colorState !== "error");
          const label = bgGenerating
            ? "🎨 DRAWING…"
            : colorState === "error"
              ? "🎨 TRY AGAIN"
              : "🎨 COLOR THIS";
          return (
            <button
              type="button"
              onClick={onColor}
              disabled={disabled}
              className="mcm-focusable"
              style={{
                width: "100%",
                padding: 16,
                background: bgGenerating ? COLOR.plum : "#F4B6C2",
                color: "#fff",
                border: "none",
                borderRadius: 14,
                fontFamily: FONT.mono,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.1em",
                cursor: disabled ? "default" : "pointer",
                marginBottom: 8,
                boxShadow: disabled ? "none" : "0 6px 16px rgba(244, 182, 194, 0.5)",
                touchAction: "manipulation",
                opacity: disabled ? 0.7 : 1,
              }}
            >
              {label}
            </button>
          );
        })()}
        {colorError && colorState === "error" && (
          <div style={{
            marginBottom: 8,
            padding: "8px 12px",
            background: COLOR.roseDeep,
            border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 8,
            color: COLOR.rose,
            fontFamily: FONT.forma,
            fontSize: 12,
            textAlign: "left",
          }}>
            {colorError}
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            padding: 12,
            background: "transparent",
            color: COLOR.muted,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          {result.isFirstDiscovery ? "✨ COOL!" : "OK"}
        </button>
      </div>
      <style>{`
        @keyframes kid-craft-pop {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes kid-craft-spin {
          from { transform: rotate(-180deg) scale(0); }
          to   { transform: rotate(0deg) scale(1); }
        }
        @keyframes kid-xp-pop {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default Craft;
